import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface ModuleContentData {
  moduleId: string;
  moduleTitle: string;
  courseId: string;
  content: string;
  contentWordCount: number;
  hasQuiz: boolean;
  hasPracticeLab: boolean;
  quizStatus?: 'success' | 'failed' | 'pending';
  practiceStatus?: 'success' | 'failed' | 'pending';
}

/**
 * Get detailed content information for a specific module
 */
export async function getModuleContent(moduleId: string): Promise<ModuleContentData | null> {
  try {
    const { data: module, error: moduleError } = await supabase
      .from('course_modules')
      .select('module_id, module_title, course_id')
      .eq('module_id', moduleId)
      .single();

    if (moduleError || !module) {
      logger.error('Module not found:', moduleId);
      return null;
    }

    const { data: content } = await supabase
      .from('module_content')
      .select('content, content_word_count')
      .eq('module_id', moduleId)
      .maybeSingle();

    const { data: quiz } = await supabase
      .from('module_quizzes')
      .select('id')
      .eq('module_id', moduleId)
      .maybeSingle();

    const { data: practice } = await supabase
      .from('generated_practice_labs')
      .select('id')
      .eq('module_id', moduleId)
      .maybeSingle();

    return {
      moduleId: module.module_id,
      moduleTitle: module.module_title,
      courseId: module.course_id,
      content: content?.content || '',
      contentWordCount: content?.content_word_count || 0,
      hasQuiz: !!quiz,
      hasPracticeLab: !!practice,
      quizStatus: quiz ? 'success' : 'pending',
      practiceStatus: practice ? 'success' : 'pending'
    };
  } catch (error) {
    logger.error('Failed to get module content:', error);
    throw error;
  }
}

/**
 * Regenerate quiz for a specific module
 */
export async function regenerateQuiz(moduleId: string) {
  try {
    logger.info('🔄 Regenerating quiz for module:', moduleId);

    const moduleData = await getModuleContent(moduleId);
    if (!moduleData || !moduleData.content) {
      throw new Error('Module content not found');
    }

    const { data, error } = await supabase.functions.invoke('generate-quiz', {
      body: {
        moduleId: moduleData.moduleId,
        courseId: moduleData.courseId,
        moduleTitle: moduleData.moduleTitle,
        moduleContent: moduleData.content
      }
    });

    if (error) throw error;

    logger.info('✅ Quiz regenerated successfully');
    return data;
  } catch (error) {
    logger.error('❌ Failed to regenerate quiz:', error);
    throw error;
  }
}

/**
 * Regenerate practice lab for a specific module
 */
export async function regeneratePracticeLab(moduleId: string) {
  try {
    logger.info('🔄 Regenerating practice lab for module:', moduleId);

    const moduleData = await getModuleContent(moduleId);
    if (!moduleData || !moduleData.content) {
      throw new Error('Module content not found');
    }

    const { data, error } = await supabase.functions.invoke('generate-practice-lab', {
      body: {
        moduleId: moduleData.moduleId,
        courseId: moduleData.courseId,
        moduleTitle: moduleData.moduleTitle,
        moduleContent: moduleData.content
      }
    });

    if (error) throw error;

    logger.info('✅ Practice lab regenerated successfully');
    return data;
  } catch (error) {
    logger.error('❌ Failed to regenerate practice lab:', error);
    throw error;
  }
}

/**
 * Bulk regenerate quizzes for multiple modules
 */
export async function bulkRegenerateQuizzes(moduleIds: string[]) {
  try {
    logger.info(`🔄 Bulk regenerating quizzes for ${moduleIds.length} modules`);

    const results = {
      successful: 0,
      failed: 0,
      errors: [] as string[]
    };

    for (const moduleId of moduleIds) {
      try {
        await regenerateQuiz(moduleId);
        results.successful++;
      } catch (error) {
        results.failed++;
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        results.errors.push(`${moduleId}: ${errorMsg}`);
      }
    }

    logger.info('✅ Bulk regeneration complete:', results);
    return results;
  } catch (error) {
    logger.error('❌ Failed bulk regeneration:', error);
    throw error;
  }
}

/**
 * Get all modules with issues (missing content, failed generation, etc.)
 */
export async function getProblematicModules() {
  // Return a simpler implementation to avoid type issues
  // This will be populated by the ContentReviewPanel component
  return [];
}
